import {
  CreateDotInspectionDto,
  DotInspectionFormSubmission,
  DotInspectionLookupsDto,
  DotInspectionResponseDto,
} from "../types/dotInspection";
import { PaginatedList, PaginatedQueryParams } from "../types/pagination";
import { unwrapApiResponse } from "../utils/apiUtils";
import { api } from "./apiSlice";

function buildDotInspectionFormData(
  dto: CreateDotInspectionDto,
  files?: File[],
  fileCategories?: string[],
  deletedDocumentIds?: number[]
): FormData {
  const formData = new FormData();

  formData.append("InspectionDate", dto.inspectionDate);
  formData.append("VehicleId", String(dto.vehicleId));

  if (dto.claimCompanyId != null) formData.append("ClaimCompanyId", String(dto.claimCompanyId));
  if (dto.claimCompanyName) formData.append("ClaimCompanyName", dto.claimCompanyName);
  if (dto.driverEmployeeId != null) formData.append("DriverEmployeeId", String(dto.driverEmployeeId));
  if (dto.trailerId != null) formData.append("TrailerId", String(dto.trailerId));
  if (dto.points != null) formData.append("Points", String(dto.points));
  if (dto.description) formData.append("Description", dto.description);
  if (dto.amount != null) formData.append("Amount", String(dto.amount));
  if (dto.driverCharged != null) formData.append("DriverCharged", String(dto.driverCharged));

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append("Files", file);
    });
    if (fileCategories) {
      formData.append("FileCategories", fileCategories.join(","));
    }
  }

  if (deletedDocumentIds && deletedDocumentIds.length > 0) {
    formData.append("DeletedDocumentIds", deletedDocumentIds.join(","));
  }

  return formData;
}

export const dotInspectionsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getDotInspectionsPaginated: builder.query<
      PaginatedList<DotInspectionResponseDto>,
      PaginatedQueryParams
    >({
      query: (queryParams) => {
        const params: any = {
          pageNumber: queryParams.pageNumber,
          pageSize: queryParams.pageSize,
        };

        if (
          queryParams.filters &&
          Object.keys(queryParams.filters).length > 0
        ) {
          Object.entries(queryParams.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
              params[`filters[${key}]`] = value;
            }
          });
        }

        return {
          url: "dotInspections",
          params,
        };
      },
      transformResponse: unwrapApiResponse,
      providesTags: ["DotInspections"],
    }),

    getDotInspectionLookups: builder.query<DotInspectionLookupsDto, void>({
      query: () => "dotInspections/lookups",
      transformResponse: unwrapApiResponse,
      providesTags: ["DotInspections"],
    }),

    getDotInspectionById: builder.query<DotInspectionResponseDto, number>({
      query: (id) => `dotInspections/${id}`,
      transformResponse: unwrapApiResponse,
      providesTags: (result, error, id) => [{ type: "DotInspections", id }],
    }),

    createDotInspection: builder.mutation<
      DotInspectionResponseDto,
      DotInspectionFormSubmission
    >({
      query: ({ dto, files, fileCategories }) => ({
        url: "dotInspections",
        method: "POST",
        body: buildDotInspectionFormData(dto, files, fileCategories),
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["DotInspections", "Documents"],
    }),

    updateDotInspection: builder.mutation<
      DotInspectionResponseDto,
      { id: number } & DotInspectionFormSubmission
    >({
      query: ({ id, dto, files, fileCategories, deletedDocumentIds }) => ({
        url: `dotInspections/${id}`,
        method: "PUT",
        body: buildDotInspectionFormData(
          dto,
          files,
          fileCategories,
          deletedDocumentIds
        ),
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: (result, error, { id }) => [
        "DotInspections",
        { type: "DotInspections", id },
        "Documents",
      ],
    }),

    deleteDotInspection: builder.mutation<void, number>({
      query: (id) => ({
        url: `dotInspections/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["DotInspections"],
    }),
  }),
});

export const {
  useGetDotInspectionsPaginatedQuery,
  useGetDotInspectionLookupsQuery,
  useGetDotInspectionByIdQuery,
  useCreateDotInspectionMutation,
  useUpdateDotInspectionMutation,
  useDeleteDotInspectionMutation,
} = dotInspectionsApi;
